import axios from '@core/services/axios';
import {Api} from '@common/constants';
import store from '@store';
import {CaseAuditHistoryParams} from '@pages/case-audit-history';

// Helper to get tenantId and userId from store
const getTenantId = () => store.getState().auth?.user?.tenantId;
const getUserId = () => store.getState().auth?.user?.id;

// Case interface based on API response
export interface Case {
  caseId: string;
  workflowId: string;
  workflowName: string;
  createdDate: string;
  assignedPersona: string;
  assignee: string;
  status: string;
}

// Case Detail interface based on API response
export interface CaseDetail {
  id: string;
  caseId: string;
  workflowId: string;
  workflowName: string;
  workflowVersion: string;
  inputPayload: string;
  triggeredRuleCondition: string;
  assignedPersona: string;
  assignee: string;
  webhookUrl: string;
  status: string;
  comments: string | null;
  reviewedBy: string | null;
  createdTimestamp: string;
}

// API response interface
export interface CasesResponse {
  error: boolean;
  code: string;
  data: {
    content: Case[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  message: string;
}

// Fetch cases with pagination, sorting, and search
export const fetchCasesApi = async (
  page: number = 0,
  size: number = 10,
  sortBy: string = 'createdDate',
  sortDirection: 'ASC' | 'DESC' = 'DESC',
  search: string = ''
) => {
  const tenantId = getTenantId();
  const userId = getUserId();

  const response = await axios.get(`${Api.RULE_ENGINE_BASE_URL}${Api.CASES}`, {
    headers: {
      'X-Tenant-ID': tenantId,
      'X-User-ID': userId,
      'X-User-Role': 'Admin', // You might want to get this from store as well
    },
    params: {
      page,
      size,
      sortBy,
      sortDirection,
      ...(search && {search}),
    },
  });

  return response.data as CasesResponse;
};

// Fetch a single case by ID
export const fetchCaseByIdApi = async (caseId: string) => {
  const tenantId = getTenantId();
  const userId = getUserId();

  const response = await axios.get(
    `${Api.RULE_ENGINE_BASE_URL}${Api.CASES}/${caseId}`,
    {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId,
        'X-User-Role': 'Admin',
      },
    }
  );

  return response.data;
};

// Case Detail response interface
export interface CaseDetailResponse {
  error: boolean;
  code: string;
  data: CaseDetail;
  message: string;
}

// Update case status
export const updateCaseStatusApi = async (
  caseId: string,
  status: string,
  comments?: string
) => {
  const tenantId = getTenantId();
  const userId = getUserId();

  const response = await axios.patch(
    `${Api.RULE_ENGINE_BASE_URL}${Api.CASES_V2}/${caseId}`,
    {
      status,
      ...(comments && {comments}),
    },
    {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId,
        'X-User-Role': 'Admin',
      },
    }
  );

  return response.data;
};

// Assign case to user
export const assignCaseApi = async (caseId: string, assignee: string) => {
  const tenantId = getTenantId();
  const userId = getUserId();

  const response = await axios.patch(
    `${Api.RULE_ENGINE_BASE_URL}${Api.CASES}/${caseId}/assign`,
    {
      assignee,
    },
    {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId,
        'X-User-Role': 'Admin',
      },
    }
  );

  return response.data;
};

// Webhook Re-Trigger
export const reTriggerWebhook = async (caseId: string) => {
  const tenantId = getTenantId();
  const userId = getUserId();

  const response = await axios.post(
    `${Api.RULE_ENGINE_BASE_URL}${Api.CASES_V2}/${caseId}/trigger-webhook`,
    {},
    {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId,
        'X-User-Role': 'Admin',
      },
    }
  );

  return response.data;
};

// Get Case Audit History
export const getCaseAuditHistory = async (
  caseId: string,
  params?: CaseAuditHistoryParams
) => {
  const tenantId = getTenantId();
  const userId = getUserId();

  const response = await axios.get(
    `${Api.RULE_ENGINE_BASE_URL}${Api.AUDIT_CASE}/${caseId}`,
    {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId,
        'X-User-Role': 'Admin',
      },
      params,
    }
  );

  return response.data;
};
